import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, EmptyState, Icon, AutoTable, Dropdown, Status } from '@weezevent/nacre';
import { Loader } from 'semantic-ui-react';
import { DeletePayoutProfileProvider, useDeletePayoutProfileModal } from './deleteEntityModal';
import { EditPayoutProfileDrawerProvider, useEditPayoutProfileDrawer } from './editPayoutProfileDrawer';
import { LoadingStatus, useStore } from '../../store';
import { StopPropagation } from '../../../../../components/StopPropagation';
import { getFrequencyDisplayName, getPayoutMethod } from './utils';
import { PayoutMethods } from './constants';
import { formatCurrency } from '../../../../../utils/currencies';

export const Content = () => {
    const { t, i18n } = useTranslation();
    const editDrawer = useEditPayoutProfileDrawer();

    const {
        payoutProfiles: { status, data },
        bankAccounts,
        legalEntities
    } = useStore();

    const deleteModal = useDeletePayoutProfileModal();
    const { currency } = useStore();

    const renderCurrency = amount => {
        if (currency.isLoading) {
            return null;
        }

        return formatCurrency({
            currency: currency.value,
            locale: i18n.language,
            amount
        });
    };

    const columns = [
        {
            key: 'keep_in_reserve',
            label: t('sauron.financeModule.payoutProfiles.table.columns.reserve'),
            render: ({ keep_in_reserve }) => renderCurrency(keep_in_reserve)
        },
        {
            key: 'payout_frequency',
            label: t('sauron.financeModule.payoutProfiles.table.columns.frequency'),
            render: data => {
                if (getPayoutMethod(data) !== PayoutMethods.FREQUENCY) {
                    return '-';
                }

                return getFrequencyDisplayName(data.payout_frequency, { t });
            }
        },
        {
            key: 'payout_threshold',
            label: t('sauron.financeModule.payoutProfiles.table.columns.threshold'),
            render: data => {
                if (getPayoutMethod(data) !== PayoutMethods.PAYOUT_THRESHOLD) {
                    return '-';
                }

                return renderCurrency(data.payout_threshold);
            }
        },
        {
            key: 'bankAccount',
            label: t('sauron.financeModule.payoutProfiles.table.columns.bankAccount'),
            render: ({ bank_account_id }) => {
                return bankAccounts.getById(bank_account_id)?.name || '-';
            }
        },
        {
            key: 'financeEntity',
            label: t('sauron.financeModule.payoutProfiles.table.columns.financeEntity'),
            render: ({ document_head_id }) => {
                return legalEntities.getById(document_head_id)?.name || '-';
            }
        },
        {
            key: 'active',
            label: t('sauron.financeModule.payoutProfiles.table.columns.active'),
            render: () => <Status color="green" text={t('sauron.financeModule.payoutProfiles.table.status.active')} />
        },

        {
            key: 'actions',
            label: '',
            render: data => {
                return (
                    <StopPropagation>
                        <Dropdown>
                            <Dropdown.Item onClick={() => editDrawer.edit(data)}>{t('common.cta.edit')}</Dropdown.Item>
                            <Dropdown.Item onClick={() => deleteModal.open(data.id)}>{t('common.cta.delete')}</Dropdown.Item>
                        </Dropdown>
                    </StopPropagation>
                );
            }
        }
    ];

    const renderContent = () => {
        if (status === LoadingStatus.EMPTY) {
            return (
                <EmptyState
                    title={t('sauron.financeModule.payoutProfiles.empty.title')}
                    icon={<Icon margin name="list" size="regular" />}
                    labelButton={t('sauron.financeModule.payoutProfiles.cta.add')}
                    onClick={editDrawer.create}
                />
            );
        }

        if (status === LoadingStatus.LOADING) {
            return <Loader active inline="centered" />;
        }

        return (
            <>
                <AutoTable useNew objects={data} columns={columns} />
                <Button as="button" primary onClick={editDrawer.create}>
                    {t('sauron.financeModule.payoutProfiles.cta.add')}
                </Button>
            </>
        );
    };

    return (
        <Card>
            <Card.Header>
                <Card.Title>{t('sauron.financeModule.payoutProfiles.title')}</Card.Title>
            </Card.Header>
            <Card.Content>{renderContent()}</Card.Content>
        </Card>
    );
};

export const PayoutProfiles = () => {
    return (
        <DeletePayoutProfileProvider>
            <EditPayoutProfileDrawerProvider>
                <Content />
            </EditPayoutProfileDrawerProvider>
        </DeletePayoutProfileProvider>
    );
};
